import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Box, Text, Badge, Switch,
  Tabs, TabList, TabPanels, Tab, TabPanel, Input, useToast, Flex, Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { FiMenu, FiCheck } from 'react-icons/fi';
import { DEFAULT_OUT, DEFAULT_IN, saveStatusConfig, fetchStatusConfig } from '../hooks/useInvoiceStatuses';

const COLOR_OPTS = ['gray','yellow','orange','red','green','blue','purple','cyan','teal','pink'];

function StatusList({ statuses, onChange }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setDragOver(null); return; }
    const arr = [...statuses];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx, 0, moved);
    onChange(arr.map((s, i) => ({ ...s, sort: i + 1 })));
    setDragIdx(null); setDragOver(null);
  };

  return (
    <VStack spacing={1} align="stretch">
      {statuses.map((s, idx) => (
        <Flex key={s.key} align="center" gap={2} p={2} borderRadius="md"
          border="1px" borderColor={dragOver === idx ? 'blue.300' : 'gray.100'}
          bg={dragOver === idx ? 'blue.50' : s.enabled ? 'white' : 'gray.50'}
          opacity={s.enabled ? 1 : 0.5}
          draggable
          onDragStart={() => setDragIdx(idx)}
          onDragOver={e => { e.preventDefault(); setDragOver(idx); }}
          onDragLeave={() => setDragOver(null)}
          onDrop={() => handleDrop(idx)}
          onDragEnd={() => { setDragIdx(null); setDragOver(null); }}>
          <Box color="gray.300" cursor="grab" flexShrink={0}><FiMenu size={13} /></Box>
          <Switch size="sm" colorScheme="teal" isChecked={!!s.enabled}
            onChange={e => onChange(statuses.map((x, i) => i === idx ? { ...x, enabled: e.target.checked } : x))} />
          <Box flex={1} minW={0}>
            <Input size="xs" value={s.label} fontWeight="600"
              onChange={e => onChange(statuses.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
          </Box>
          <HStack spacing={1} flexShrink={0}>
            {COLOR_OPTS.map(c => (
              <Box key={c} w="14px" h="14px" borderRadius="full"
                bg={`${c}.400`} cursor="pointer"
                border={s.color === c ? '2px solid' : '1px solid'}
                borderColor={s.color === c ? 'gray.800' : 'transparent'}
                onClick={() => onChange(statuses.map((x, i) => i === idx ? { ...x, color: c } : x))} />
            ))}
          </HStack>
          <Badge colorScheme={s.color} fontSize="8px" flexShrink={0}>{s.label.slice(0,12)}</Badge>
        </Flex>
      ))}
    </VStack>
  );
}

export default function InvoiceStatusSettingsModal({ isOpen, onClose }) {
  const [outStatuses, setOutStatuses] = useState([]);
  const [inStatuses, setInStatuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    Promise.all([fetchStatusConfig('out'), fetchStatusConfig('in')]).then(([out, inn]) => {
      setOutStatuses(out); setInStatuses(inn); setLoading(false);
    });
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveStatusConfig('out', outStatuses),
        saveStatusConfig('in', inStatuses),
      ]);
      toast({ title: 'Invoice status settings saved', status: 'success', duration: 2000 });
      onClose();
    } catch(e) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    }
    setSaving(false);
  };

  const handleReset = (dir) => {
    if (dir === 'out') setOutStatuses(DEFAULT_OUT.map((s, i) => ({ ...s, sort: i + 1 })));
    else setInStatuses(DEFAULT_IN.map((s, i) => ({ ...s, sort: i + 1 })));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">Invoice Status Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Control which invoice statuses appear throughout the app — in the Bookkeeper, project invoices, dashboards and modals.
            Drag rows to reorder. Toggle to show/hide. Edit labels and colours.
          </Text>
          {loading ? <Text>Loading…</Text> : (
            <Tabs colorScheme="brand">
              <TabList>
                <Tab fontSize="sm" fontWeight="700">£ IN — Revenue (Client Invoices)</Tab>
                <Tab fontSize="sm" fontWeight="700">£ OUT — Payments (Supplier Invoices)</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" color="gray.500">Drag to reorder · Toggle to show/hide · Click colour dot to change</Text>
                    <Button size="xs" variant="ghost" onClick={() => handleReset('out')}>Reset to defaults</Button>
                  </HStack>
                  <StatusList statuses={outStatuses} onChange={setOutStatuses} />
                </TabPanel>
                <TabPanel px={0}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" color="gray.500">Drag to reorder · Toggle to show/hide · Click colour dot to change</Text>
                    <Button size="xs" variant="ghost" onClick={() => handleReset('in')}>Reset to defaults</Button>
                  </HStack>
                  <StatusList statuses={inStatuses} onChange={setInStatuses} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="teal" leftIcon={<FiCheck />} isLoading={saving} onClick={handleSave}>
            Save Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
